/**
 * <s-gallery-slider> — product page editorial slider.
 *
 * One photo at a time in a native scroll-snap track; shoppers swipe, scroll or use the
 * prev/next buttons. Buttons target this element with commandfor + command="--prev"/"--next".
 * Without JavaScript the track still swipes/scrolls natively.
 *
 * Required markup:
 *   [data-gallery-track]    — horizontal scroll-snap track; each direct child is one slide.
 *   [data-gallery-current]  — optional, receives the 1-based current slide number.
 *   [data-gallery-progress] — optional, width is set to the progress percentage.
 *   [data-gallery-prev] / [data-gallery-next] — optional buttons, disabled at the ends.
 */
class SGallerySlider extends HTMLElement {
  #ac;
  #frame = 0;
  #index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const track = this.#track;
    if (!track) return;

    this.addEventListener('command', this.#handleCommand, opts);
    this.addEventListener('click', this.#handleClickFallback, opts);
    track.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get #track() {
    return this.querySelector('[data-gallery-track]');
  }

  get #count() {
    return this.#track?.children.length ?? 0;
  }

  #handleCommand = (event) => {
    if (event.command === '--next') this.#go(this.#index + 1);
    if (event.command === '--prev') this.#go(this.#index - 1);
  };

  // Browsers without command invokers: fall back to plain clicks on the same buttons.
  #handleClickFallback = (event) => {
    if ('command' in HTMLButtonElement.prototype) return;
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-gallery-next]')) this.#go(this.#index + 1);
    if (event.target.closest('[data-gallery-prev]')) this.#go(this.#index - 1);
  };

  #handleScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #go(index) {
    const track = this.#track;
    if (!track) return;
    const target = Math.max(0, Math.min(this.#count - 1, index));
    const slide = track.children[target];
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
  }

  #update() {
    const track = this.#track;
    if (!track) return;
    const width = track.clientWidth || 1;
    const count = this.#count;
    this.#index = Math.max(0, Math.min(count - 1, Math.round(Math.abs(track.scrollLeft) / width)));

    const current = this.querySelector('[data-gallery-current]');
    if (current) current.textContent = String(this.#index + 1);

    const progress = this.querySelector('[data-gallery-progress]');
    if (progress instanceof HTMLElement && count > 0) {
      progress.style.width = `${((this.#index + 1) / count) * 100}%`;
    }

    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev instanceof HTMLButtonElement) prev.disabled = this.#index === 0;
    if (next instanceof HTMLButtonElement) next.disabled = this.#index >= count - 1;
  }
}

if (!customElements.get('s-gallery-slider')) {
  customElements.define('s-gallery-slider', SGallerySlider);
}
